import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import FullPageLoader from './components/FullPageLoader';
import PrivateRoute from './components/PrivateRoute';
import DocumentFormViewer from './pages/dashboard/form/DocumentFormViewer';
import FormViewer from './pages/dashboard/form/FormViewer';

const DashboardLayout = lazy(() => import('./components/DashboardLayout'));
const HomeRedirect = lazy(() => import('./components/HomeRedirect'));
const AuthenticatedLayout = lazy(() => import('./components/AuthenticatedLayout'));
const PublicLayout = lazy(() => import('./components/PublicLayout'));

const SignIn = lazy(() => import('./pages/auth/SignInForm'));
const SignUp = lazy(() => import('./pages/auth/SignUpForm'));
const Home = lazy(() => import('./pages/Home'));

const ChangePasswordForm = lazy(() => import('./pages/profiles/ChangePasswordForm'));
const EditProfileForm = lazy(() => import('./pages/profiles/ProfileEditForm'));
const SettingsPage = lazy(() => import('./pages/profiles/SettingsPage'));

const AddEditInventoryItem = lazy(() => import('./pages/dashboard/Inventory/AddEditInventoryItem'));
const InventoryDashboard = lazy(() => import('./pages/dashboard/Inventory/InventoryDashboard'));
const InventoryItemDisplay = lazy(() => import('./pages/dashboard/Inventory/InventoryItemDisplay'));
const TemplateManagerPage = lazy(() => import('./pages/dashboard/Inventory/TemplateManagerPage'));

const FormBuilder = lazy(() => import('./pages/dashboard/form/FormBuilder'));
const FormDashboard = lazy(() => import('./pages/dashboard/form/FormDashboard'));
const FormThemeBuilder = lazy(() => import('./pages/dashboard/form/FormThemeBuilder'));
const LeadDetail = lazy(() => import('./pages/dashboard/Leads/LeadDetail'));
const LeadsDashboard = lazy(() => import('./pages/dashboard/Leads/LeadsDashboard'));
const LeadsStatistics = lazy(() => import('./pages/dashboard/Leads/LeadsStatistics'));
const NotificationsPage = lazy(() => import('./pages/dashboard/Notifications'));
const TeamDashboard = lazy(() => import('./pages/dashboard/TeamDashboard'));

function App() {
  return (
    <Router>
      <ToastContainer position="top-right" autoClose={3000} />
      <Suspense fallback={<FullPageLoader />}>
        <Routes>
          {/* Legacy form ID URL (backward compatible) */}
          <Route path="/form/:formId" element={<FormViewer />} />

          {/* Public layout with NavBar - Must come before catch-all route */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/home" element={<Home />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/profile/edit/:uid" element={<EditProfileForm />} />
            <Route path="/profile/password/:uid" element={<ChangePasswordForm />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Document request form (lead-specific upload link): /businessname/formname/leadId */}
          <Route path="/:businessSlug/:formSlug/:leadId" element={<DocumentFormViewer />} />
          {/* Path-based lead form URL: /businessname/formname */}
          <Route path="/:businessSlug/:formSlug" element={<FormViewer />} />

          {/* Protected dashboard routes */}
          <Route path="/dashboard" element={<PrivateRoute />}>
            <Route element={<AuthenticatedLayout><DashboardLayout /></AuthenticatedLayout>}>
              <Route index element={<Navigate to="inventory" />} />
              <Route path="inventory" element={<InventoryDashboard />} />
              <Route path="inventory/add" element={<AddEditInventoryItem />} />
              <Route path="inventory/template-manager" element={<TemplateManagerPage />} />
              <Route path="inventory/:itemId" element={<InventoryItemDisplay />} />
              <Route path="leads" element={<LeadsDashboard />} />
              <Route path="leads/statistics" element={<LeadsStatistics />} />
              <Route path="leads/:leadId" element={<LeadDetail />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="teams" element={<TeamDashboard />} />
              <Route path="forms" element={<FormDashboard />} />
              <Route path="forms/form-builder" element={<FormBuilder />} />
              <Route path="forms/form-builder/:formId?" element={<FormBuilder />} />
              <Route path="forms/form-theme-builder" element={<FormThemeBuilder />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
